import React, { useState, useContext, useEffect } from "react";
import { Link as RouterLink } from "react-router-dom";
import Button from "@material-ui/core/Button";
import CssBaseline from "@material-ui/core/CssBaseline";
import TextField from "@material-ui/core/TextField";
import Link from "@material-ui/core/Link";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import ColorModeContext from "../../layout/themeContext";
import useSettings from "../../hooks/useSettings";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import InputAdornment from "@material-ui/core/InputAdornment";
import IconButton from "@material-ui/core/IconButton";
import { Helmet } from "react-helmet";
import { getBackendUrl } from "../../config";
import packageJson from "../../../package.json";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import ChatIcon from "@material-ui/icons/Chat";
import SpeedIcon from "@material-ui/icons/Speed";
import GroupIcon from "@material-ui/icons/Group";
import Brightness4Icon from "@material-ui/icons/Brightness4";
import Brightness7Icon from "@material-ui/icons/Brightness7";
import TranslateIcon from "@material-ui/icons/Translate";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";

const useStyles = makeStyles((theme) => {
    return ({
    root: {
        width: "100vw",
        height: "100vh",
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: theme.palette.background ? theme.palette.background.default : (theme.palette.type === "light" ? "#f5f5f5" : "#303030"),
        padding: "0",
        boxSizing: "border-box",
        overflow: "auto",
        position: "relative",
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
        backgroundPosition: "center center",
        
        [theme.breakpoints.down("sm")]: {
            flexDirection: "column",
        },
    },

    "@keyframes slideInUp": {
        from: {
            opacity: 0,
            transform: "translateY(30px)",
        },
        to: {
            opacity: 1,
            transform: "translateY(0)",
        },
    },

    "@keyframes float": {
        "0%, 100%": { transform: "translateY(0px)" },
        "50%": { transform: "translateY(-15px)" },
    },

    containerLogin: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        height: "100%",
        padding: "20px",
    },

    paper: {
        backgroundColor: theme.palette.type === "dark" ? "#1e1e1e" : "white",
        boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
        display: "flex",
        flexDirection: "row",
        borderRadius: "20px",
        overflow: "hidden",
        animation: "$slideInUp 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
        width: "100%",
        maxWidth: "850px",
        minHeight: "500px",
        
        [theme.breakpoints.down("md")]: {
            flexDirection: "column",
            maxWidth: "500px",
            minHeight: "auto",
        },
    },

    leftSection: {
        flex: 1,
        padding: "40px 30px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: theme.palette.type === "dark" ? "#1e1e1e" : "white",
        position: "relative",
        
        [theme.breakpoints.down("md")]: {
            padding: "30px 25px",
        },
    },
    
    leftContent: {
        width: "100%",
        maxWidth: "360px",
        display: "flex",
        flexDirection: "column",
    },

    rightSection: {
        flex: 1,
        background: "linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)",
        padding: "40px 30px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        color: "white",
        position: "relative",
        overflow: "hidden",
        
        "&::before": {
            content: '""',
            position: "absolute",
            top: "-50%",
            right: "-50%",
            width: "200%",
            height: "200%",
            background: `radial-gradient(circle, ${theme.palette.primary.main}20 0%, transparent 70%)`,
            animation: "$float 6s ease-in-out infinite",
        },
        
        [theme.breakpoints.down("md")]: {
            padding: "30px 25px",
            minHeight: "300px",
        },
    },

    logoImg: {
        maxWidth: "140px",
        maxHeight: "60px",
        height: "auto",
        width: "auto",
        marginBottom: "24px",
        objectFit: "contain",
        filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.1))",
        display: "block",
        marginLeft: "auto",
        marginRight: "auto",
        content: "url(" + (theme.mode === "light"
            ? theme.calculatedLogoLight()
            : theme.calculatedLogoDark()) + ")",
    },

    loginTitle: {
        fontSize: "24px",
        fontWeight: 700,
        color: theme.palette.type === "dark" ? "#f0f0f0" : "#1a1a1a",
        marginBottom: "6px",
    },

    loginSubtitle: {
        fontSize: "13px",
        color: theme.palette.type === "dark" ? "#aaa" : "#666",
        marginBottom: "24px",
    },

    form: {
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: "12px",
    },

    textField: {
        "& .MuiOutlinedInput-root": {
            borderRadius: "10px",
            backgroundColor: theme.palette.type === "dark" ? "#2a2a2a" : "#f8f9fa",
            transition: "all 0.3s ease",
            "&:hover": {
                backgroundColor: theme.palette.type === "dark" ? "#333" : "#f0f1f3",
            },
            "&.Mui-focused": {
                backgroundColor: theme.palette.type === "dark" ? "#2a2a2a" : "white",
                boxShadow: `0 0 0 3px ${theme.palette.primary.main}15`,
            },
            "& fieldset": {
                borderColor: theme.palette.type === "dark" ? "#444" : "#e0e0e0",
            },
            "&:hover fieldset": {
                borderColor: theme.palette.primary.main,
            },
            "&.Mui-focused fieldset": {
                borderColor: theme.palette.primary.main,
                borderWidth: "2px",
            },
        },
        "& .MuiOutlinedInput-input": {
            padding: "14px 14px",
            fontSize: "14px",
            color: theme.palette.type === "dark" ? "#f0f0f0" : "inherit",
        },
        "& .MuiInputLabel-root": {
            color: theme.palette.type === "dark" ? "#aaa" : "#666",
            fontWeight: 500,
            fontSize: "14px",
            transform: "translate(14px, 15px) scale(1)",
            "&.MuiInputLabel-shrink": {
                transform: "translate(14px, -6px) scale(0.75)",
            },
            "&.Mui-focused": {
                color: theme.palette.primary.main,
            },
        },
    },

    forgotPassword: {
        color: theme.palette.primary.main,
        textDecoration: "none",
        fontSize: "13px",
        fontWeight: 600,
        display: "block",
        textAlign: "right",
        marginTop: "4px",
        "&:hover": {
            textDecoration: "underline",
        },
    },

    submit: {
        marginTop: "12px",
        background: theme.palette.primary.main,
        color: "white",
        borderRadius: "10px",
        padding: "12px 0",
        fontSize: "15px",
        fontWeight: 600,
        textTransform: "none",
        boxShadow: `0 4px 12px ${theme.palette.primary.main}40`,
        transition: "all 0.3s ease",
        "&:hover": {
            background: theme.palette.primary.dark,
            transform: "translateY(-2px)",
            boxShadow: `0 6px 16px ${theme.palette.primary.main}50`,
        },
    },

    divider: {
        display: "flex",
        alignItems: "center",
        margin: "16px 0",
        "&::before, &::after": {
            content: '""',
            flex: 1,
            height: "1px",
            backgroundColor: theme.palette.type === "dark" ? "#444" : "#e0e0e0",
        },
    },

    dividerText: {
        padding: "0 12px",
        color: theme.palette.type === "dark" ? "#888" : "#999",
        fontSize: "12px",
    },

    registerButton: {
        width: "100%",
        padding: "12px 0",
        borderRadius: "10px",
        border: `2px solid ${theme.palette.primary.main}`,
        backgroundColor: "transparent",
        color: theme.palette.primary.main,
        fontSize: "15px",
        fontWeight: 600,
        cursor: "pointer",
        transition: "all 0.3s ease",
        textDecoration: "none",
        display: "block",
        textAlign: "center",
        "&:hover": {
            backgroundColor: `${theme.palette.primary.main}08`,
        },
    },

    rightContent: {
        position: "relative",
        zIndex: 1,
        textAlign: "center",
    },

    whatsappIcon: {
        fontSize: "80px",
        color: "white",
        marginBottom: "24px",
        opacity: 0.95,
        filter: "drop-shadow(0 4px 12px rgba(255, 255, 255, 0.2))",
    },

    rightTitle: {
        fontSize: "26px",
        fontWeight: 700,
        marginBottom: "12px",
        lineHeight: "1.3",
        textAlign: "center",
    },

    rightSubtitle: {
        fontSize: "14px",
        opacity: 0.85,
        lineHeight: "1.6",
        maxWidth: "320px",
        margin: "0 auto 32px",
        textAlign: "center",
    },

    features: {
        display: "flex",
        flexDirection: "column",
        gap: "14px",
        width: "100%",
        maxWidth: "340px",
        margin: "0 auto",
    },

    feature: {
        display: "flex",
        alignItems: "center",
        gap: "14px",
        padding: "14px 18px",
        backgroundColor: "rgba(255, 255, 255, 0.08)",
        borderRadius: "12px",
        backdropFilter: "blur(10px)",
        border: "1px solid rgba(255, 255, 255, 0.1)",
        transition: "all 0.3s ease",
        "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.12)",
            transform: "translateX(5px)",
        },
    },

    featureIcon: {
        fontSize: "24px",
        opacity: 0.9,
        flexShrink: 0,
    },

    featureText: {
        fontSize: "13px",
        textAlign: "left",
        fontWeight: 500,
        opacity: 0.9,
        lineHeight: "1.4",
    },

    themeToggle: {
        color: theme.palette.type === "dark" ? "#f0f0f0" : "#555",
        backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)",
        "&:hover": {
            backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.1)",
        },
    },

    topActions: {
        position: "absolute",
        top: "12px",
        right: "12px",
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: "4px",
        zIndex: 10,
    },

    langToggle: {
        color: theme.palette.type === "dark" ? "#f0f0f0" : "#555",
        backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)",
        "&:hover": {
            backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.1)",
        },
    },

    versionTag: {
        position: "absolute",
        bottom: "12px",
        right: "16px",
        fontSize: "11px",
        color: theme.palette.type === "dark" ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.3)",
        fontWeight: 500,
        zIndex: 10,
    },
})});

const Login = () => {
    const classes = useStyles();
    const theme = useTheme();
    const { colorMode } = useContext(ColorModeContext);
    const { appLogoFavicon, appName, mode } = colorMode;
    const [user, setUser] = useState({ email: "", password: "" });
    const [showPassword, setShowPassword] = useState(false);
    const [allowSignup, setAllowSignup] = useState(false);
    const [langAnchorEl, setLangAnchorEl] = useState(null);
    const { getPublicSetting } = useSettings();
    const { handleLogin } = useContext(AuthContext);

    const [backgroundLight, setBackgroundLight] = useState("");
    const [backgroundDark, setBackgroundDark] = useState("");

    const getCompanyIdFromUrl = () => {
        const urlParams = new URLSearchParams(window.location.search);
        const companyId = urlParams.get("companyId");
        return companyId ? parseInt(companyId) : null;
    };

    const handleChangeInput = (e) => {
        setUser({ ...user, [e.target.name]: e.target.value });
    };

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const handlSubmit = (e) => {
        e.preventDefault();
        handleLogin(user);
    };

    const handleLanguageChange = (lng) => {
        i18n.changeLanguage(lng);
        localStorage.setItem("i18nextLng", lng);
        setLangAnchorEl(null);
    };

    useEffect(() => {
        const companyId = getCompanyIdFromUrl();

        getPublicSetting("userCreation", companyId)
            .then((data) => {
                setAllowSignup(data === "enabled");
            })
            .catch((error) => {
                console.log("Error reading setting", error);
            });

        getPublicSetting("appLogoBackgroundLight", companyId)
            .then((bgLight) => {
                if (bgLight) {
                    const backendUrl = getBackendUrl();
                    const fullUrl = backendUrl + "/public/" + bgLight;
                    console.log("Background light URL:", fullUrl);
                    setBackgroundLight(fullUrl);
                } else {
                    setBackgroundLight("");
                }
            })
            .catch((err) => {
                console.error("Erro ao carregar imagem de fundo clara:", err);
                setBackgroundLight("");
            });

        getPublicSetting("appLogoBackgroundDark", companyId)
            .then((bgDark) => {
                if (bgDark) {
                    const backendUrl = getBackendUrl();
                    const fullUrl = backendUrl + "/public/" + bgDark;
                    console.log("Background dark URL:", fullUrl);
                    setBackgroundDark(fullUrl);
                } else {
                    setBackgroundDark("");
                }
            })
            .catch((err) => {
                console.error("Erro ao carregar imagem de fundo escura:", err);
                setBackgroundDark("");
            });
    }, []);

    let finalBackground;
    
    console.log("Modo:", mode);
    console.log("Background Light URL:", backgroundLight);
    console.log("Background Dark URL:", backgroundDark);
    
    const hasValidLightBg = backgroundLight && backgroundLight.trim() !== '';
    const hasValidDarkBg = backgroundDark && backgroundDark.trim() !== '';
    
    if (mode === "light") {
        if (hasValidLightBg) {
            finalBackground = `url(${backgroundLight})`;
            console.log("Usando background light:", finalBackground);
        } else {
            finalBackground = theme.palette.background ? theme.palette.background.default : "#f5f5f5";
            console.log("Usando cor do tema light:", finalBackground);
        }
    } else {
        if (hasValidDarkBg) {
            finalBackground = `url(${backgroundDark})`;
            console.log("Usando background dark:", finalBackground);
        } else {
            finalBackground = theme.palette.background ? theme.palette.background.default : "#303030";
            console.log("Usando cor do tema dark:", finalBackground);
        }
    }
    
    console.log("Background final definido:", finalBackground);


    return (
        <>
            <Helmet>
                <title>{appName || "Multi100"}</title>
                <link rel="icon" href={appLogoFavicon || "/default-favicon.ico"} />
            </Helmet>

            <div 
                className={classes.root}
                style={{
                    backgroundImage: typeof finalBackground === "string" && finalBackground.includes("url(") 
                        ? finalBackground 
                        : "none"
                }}
            >
                <Container
                    component="main"
                    maxWidth={false}
                    className={classes.containerLogin}
                >
                    <CssBaseline />
                    <div className={classes.paper}>
                        {/* Left Section - Login Form */}
                        <div className={classes.leftSection}>
                            <div className={classes.topActions}>
                                <IconButton
                                    className={classes.langToggle}
                                    onClick={(e) => setLangAnchorEl(e.currentTarget)}
                                    aria-label="alterar idioma"
                                    size="small"
                                >
                                    <TranslateIcon fontSize="small" />
                                </IconButton>
                                <Menu
                                    anchorEl={langAnchorEl}
                                    open={Boolean(langAnchorEl)}
                                    onClose={() => setLangAnchorEl(null)}
                                >
                                    <MenuItem onClick={() => handleLanguageChange("pt-BR")}>Português</MenuItem>
                                    <MenuItem onClick={() => handleLanguageChange("en")}>English</MenuItem>
                                    <MenuItem onClick={() => handleLanguageChange("es")}>Español</MenuItem>
                                    <MenuItem onClick={() => handleLanguageChange("ar")}>عربي</MenuItem>
                                </Menu>
                                <IconButton
                                    className={classes.themeToggle}
                                    onClick={colorMode.toggleColorMode}
                                    aria-label="alternar tema"
                                    size="small"
                                >
                                    {mode === "dark" ? <Brightness7Icon fontSize="small" /> : <Brightness4Icon fontSize="small" />}
                                </IconButton>
                            </div>
                            <div className={classes.leftContent}>
                                <img className={classes.logoImg} alt="logo" />
                                
                                <div className={classes.loginTitle}>
                                    {i18n.t("login.title") || "Bem-vindo de volta"}
                                </div>
                                <div className={classes.loginSubtitle}>
                                    Entre com suas credenciais para acessar
                                </div>

                                <form className={classes.form} noValidate onSubmit={handlSubmit}>
                                    <TextField
                                        variant="outlined"
                                        margin="normal"
                                        required
                                        fullWidth
                                        id="email"
                                        label={i18n.t("login.form.email")}
                                        name="email"
                                        value={user.email}
                                        onChange={handleChangeInput}
                                        autoComplete="email"
                                        autoFocus
                                        className={classes.textField}
                                    />
                                    <TextField
                                        variant="outlined"
                                        margin="normal"
                                        required
                                        fullWidth
                                        name="password"
                                        label={i18n.t("login.form.password")}
                                        type={showPassword ? "text" : "password"}
                                        id="password"
                                        value={user.password}
                                        onChange={handleChangeInput}
                                        autoComplete="current-password"
                                        className={classes.textField}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        aria-label="toggle password visibility"
                                                        onClick={togglePasswordVisibility}
                                                        edge="end"
                                                        style={{ color: "#6b7280" }}
                                                    >
                                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                    
                                    <Link
                                        component={RouterLink}
                                        to="/forgot-password"
                                        className={classes.forgotPassword}
                                    >
                                        Esqueceu a senha?
                                    </Link>

                                    <Button
                                        type="submit"
                                        fullWidth
                                        variant="contained"
                                        color="primary"
                                        className={classes.submit}
                                    >
                                        {i18n.t("login.buttons.submit")}
                                    </Button>

                                    {allowSignup && (
                                        <>
                                            <div className={classes.divider}>
                                                <span className={classes.dividerText}>ou</span>
                                            </div>
                                            <Link
                                                component={RouterLink}
                                                to="/signup"
                                                className={classes.registerButton}
                                            >
                                                {i18n.t("login.buttons.register")}
                                            </Link>
                                        </>
                                    )}
                                </form>
                            </div>
                        </div>

                        {/* Right Section - Dark Background with Features */}
                        <div className={classes.rightSection}>
                            <div className={classes.rightContent}>
                                <WhatsAppIcon className={classes.whatsappIcon} />
                                
                                <div className={classes.rightTitle}>
                                    Conecte-se com seus clientes
                                </div>
                                <div className={classes.rightSubtitle}>
                                    Gerencie todas as suas conversas do WhatsApp em um só lugar
                                </div>

                                <div className={classes.features}>
                                    <div className={classes.feature}>
                                        <ChatIcon className={classes.featureIcon} />
                                        <div className={classes.featureText}>
                                            Atendimento multicanal integrado
                                        </div>
                                    </div>
                                    <div className={classes.feature}>
                                        <SpeedIcon className={classes.featureIcon} />
                                        <div className={classes.featureText}>
                                            Respostas rápidas e automação
                                        </div>
                                    </div>
                                    <div className={classes.feature}>
                                        <GroupIcon className={classes.featureIcon} />
                                        <div className={classes.featureText}>
                                            Gestão de equipes e departamentos
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Container>
                <span className={classes.versionTag}>v{packageJson.version}</span>
            </div>
        </>
    );
};

export default Login;